import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
      toast.success("If an account exists, a reset link has been emailed.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#FDFBF7] grain relative flex items-center justify-center px-6" data-testid="forgot-password-page">
      <Link to="/" className="absolute top-6 left-6 md:top-10 md:left-10 flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-[#FF5722]" />
        <span className="font-serif text-xl">Photo Booth <em className="text-[#FF5722]">Memories</em></span>
      </Link>
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md border border-white/10 rounded-sm p-8 md:p-10 bg-black/40 backdrop-blur"
      >
        <div className="overline text-[#FDFBF7]/60 mb-3">Studio portal</div>
        <h1 className="font-serif text-4xl md:text-5xl leading-tight">
          Reset <em className="text-[#FF5722]">password</em>.
        </h1>
        <p className="text-sm text-[#FDFBF7]/60 mt-3">
          Enter the admin email and we&apos;ll send a reset link to the studio inbox.
        </p>

        {sent ? (
          <div className="mt-8 space-y-4" data-testid="forgot-sent-confirmation">
            <div className="rounded-sm border border-white/10 bg-white/5 p-4 text-sm text-[#FDFBF7]/85">
              If an account exists for <b>{email}</b>, a reset link has been emailed. The link expires in 30 minutes.
            </div>
            <Link
              to="/admin/login"
              className="inline-block text-xs underline underline-offset-4 text-[#FDFBF7]/70 hover:text-[#FF5722]"
            >
              ← Back to sign in
            </Link>
          </div>
        ) : (
          <form className="mt-8 space-y-5" onSubmit={submit}>
            <div>
              <Label className="overline text-[#FDFBF7]/70">Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 bg-transparent border-white/20 text-[#FDFBF7] placeholder:text-white/40"
                placeholder="admin@photoboothmemories.com"
                required
                data-testid="forgot-email"
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              className="w-full inline-flex items-center justify-center gap-3 px-6 py-3 rounded-full bg-[#FDFBF7] text-[#0A0A0A] hover:bg-[#FF5722] hover:text-[#FDFBF7] transition-colors disabled:opacity-60"
              data-testid="forgot-submit"
            >
              {busy ? "Sending…" : "Send reset link"}
            </button>
            <div className="pt-2 text-center">
              <Link
                to="/admin/login"
                className="text-xs text-[#FDFBF7]/60 hover:text-[#FF5722] underline underline-offset-4"
              >
                ← Back to sign in
              </Link>
            </div>
          </form>
        )}
      </motion.div>
    </div>
  );
}
